// === Возрастные ворота для веба (30.08.2026) ===
//
// В приложении такие ворота стоят с 27.08 (FirstRunGate.cs), а в вебе их не было
// вообще: на blotme.app человек садился за видео-стол, и возраст у него никто не
// спрашивал. При этом во всех наших документах и в карточках магазинов заявлено 18+.
// Заявить строгий рейтинг и не исполнять его хуже, чем не заявлять вовсе — первый же
// разбор в сторе или у регулятора начнётся именно с этого расхождения.
//
// Сделано так же, как в приложении, чтобы поведение совпадало:
//   - спрашиваем ГОД РОЖДЕНИЯ, а не «подтверди, что тебе есть 18». Второй вопрос сам
//     подсказывает нужный ответ, и грош ему цена;
//   - сам год НЕ сохраняем и никуда не отправляем. В браузере остаётся только отметка
//     «проверка пройдена» или «отказано». Меньше данных — меньше обязательств;
//   - отказ запоминается: перезагрузка страницы воротá не обходит.
//
// Юридические страницы и страница удаления аккаунта открыты ДО ворот: магазины требуют,
// чтобы политику можно было прочитать не входя в сервис, а человеку, которому закрыли
// доступ, надо оставить путь удалить аккаунт и прочитать, почему так.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const PASSED = 'blotme:ageConfirmed';
const BLOCKED = 'blotme:ageBlocked';
const MIN_AGE = 18;

// Страницы, доступные без прохождения ворот.
const OPEN_PATHS = ['/privacy', '/terms', '/eula', '/rules', '/community', '/delete-account', '/get'];

const isOpenPath = (path) =>
  OPEN_PATHS.some((p) => path === p || path.startsWith(p + '/') || path.startsWith('/en' + p));

// localStorage может быть недоступен (приватное окно, запрет на сайт-данные).
// Тогда ворота показываются каждый раз — это неудобно, но безопасно.
const read = (key) => {
  try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
};
const write = (key) => {
  try { window.localStorage.setItem(key, '1'); } catch { /* переживём */ }
};

const AgeGate = () => {
  const [state, setState] = useState('checking');   // checking | ask | blocked | done
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  // Путь берём из роутера, а не из window.location: страницы меняются без перезагрузки,
  // и проверка «один раз при загрузке» обходилась бы за два клика — открыть Политику
  // (она пускает без ворот) и перейти по внутренней ссылке в игру.
  const { pathname } = useLocation();

  useEffect(() => {
    if (read(BLOCKED)) { setState('blocked'); return; }
    if (isOpenPath(pathname)) { setState('done'); return; }
    if (read(PASSED)) { setState('done'); return; }
    setState('ask');
  }, [pathname]);

  if (state === 'checking' || state === 'done') return null;

  const submit = (e) => {
    e.preventDefault();
    const value = Number(year);
    const now = new Date().getFullYear();
    if (!/^\d{4}$/.test(year) || value < 1900 || value > now) {
      setError('Введи год из четырёх цифр');
      return;
    }
    if (now - value < MIN_AGE) {
      write(BLOCKED);
      setState('blocked');
      return;
    }
    write(PASSED);
    setState('done');
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 10000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
    background: '#0F1923',
    fontFamily: "'Manrope', 'Segoe UI', system-ui, sans-serif",
    color: '#F5F5F5',
  };
  const card = {
    width: '100%', maxWidth: 420,
    background: '#172331',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 16,
    padding: '32px 26px',
    textAlign: 'center',
  };
  const badge = { color: '#E8B84B', fontWeight: 800, fontSize: 34, letterSpacing: '0.02em' };
  const title = { fontSize: 24, fontWeight: 800, margin: '14px 0 8px' };
  const text = { color: '#B8C0CC', fontSize: 15, lineHeight: 1.55, margin: 0 };
  const input = {
    width: '100%', marginTop: 20, padding: '13px 16px',
    background: '#0F1923', color: '#F5F5F5',
    border: '1px solid rgba(255,255,255,0.16)', borderRadius: 10,
    fontSize: 18, textAlign: 'center', fontFamily: 'inherit',
  };
  const button = {
    width: '100%', marginTop: 12, padding: '13px 16px',
    background: '#E8B84B', color: '#0F1923',
    border: 'none', borderRadius: 10,
    fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
  };
  const link = { color: '#E8B84B' };

  if (state === 'blocked') {
    return (
      <div style={overlay}>
        <div style={card}>
          <div style={badge}>18+</div>
          <h1 style={title}>Доступ закрыт</h1>
          <p style={text}>
            BlotMe — игра для взрослых: за столом работает живая видео-связь с незнакомыми
            людьми. Приходи, когда исполнится 18.
          </p>
          <p style={{ ...text, marginTop: 16, fontSize: 14 }}>
            Считаешь, что произошла ошибка — напиши на{' '}
            <a href="mailto:support@blotme.app" style={link}>support@blotme.app</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay}>
      <form style={card} onSubmit={submit}>
        <div style={badge}>18+</div>
        <h1 style={title}>Сколько тебе лет?</h1>
        <p style={text}>
          BlotMe — игра с рейтингом 18+. Укажи год своего рождения.
        </p>
        <input
          style={input}
          inputMode="numeric"
          autoFocus
          placeholder="1990"
          value={year}
          maxLength={4}
          onChange={(e) => { setYear(e.target.value.replace(/\D/g, '')); setError(''); }}
          aria-label="Год рождения"
        />
        {error && (
          <p style={{ ...text, color: '#F87171', marginTop: 10, fontSize: 14 }}>{error}</p>
        )}
        <button type="submit" style={button}>Продолжить</button>
        <p style={{ ...text, marginTop: 16, fontSize: 13 }}>
          Год мы не сохраняем — он нужен только для этой проверки.{' '}
          <a href="/privacy" style={link}>Политика конфиденциальности</a>
        </p>
      </form>
    </div>
  );
};

export default AgeGate;
